// File: src/pages/candidate/FollowedCompaniesPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FollowedCompaniesPage() {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

    useEffect(() => {
        const fetchFollowed = async () => {
            const token = localStorage.getItem('skillmatch_token');
            if (!token) return navigate('/login');
            try {
                const res = await fetch(`${API_URL}/api/users/followed-companies`, {
                    headers: { 'x-auth-token': token }
                });
                if (res.ok) setCompanies(await res.json());
            } catch (error) { console.error("Lỗi:", error); } finally { setLoading(false); }
        };
        fetchFollowed();
    }, [navigate, API_URL]);

    const handleUnfollow = async (id, e) => {
        e.stopPropagation();
        const token = localStorage.getItem('skillmatch_token');
        if(!window.confirm("Bạn có chắc muốn hủy theo dõi công ty này?")) return;
        
        try {
            await fetch(`${API_URL}/api/users/toggle-follow/${id}`, { method: 'PUT', headers: { 'x-auth-token': token } });
            setCompanies(companies.filter(c => c._id !== id));
        } catch (err) { console.error(err); }
    };

    if (loading) return (
        <div className="min-h-screen pt-24 flex items-center justify-center bg-[#F8FAFC]">
            <span className="material-symbols-outlined animate-spin text-3xl text-blue-600">sync</span>
        </div>
    );

    return (
        <div className="bg-[#F8FAFC] min-h-screen pt-28 pb-12 px-6"> {/* Nền xám nhẹ tạo tương phản */}
            <div className="max-w-6xl mx-auto">
                
                {/* Header có Gradient Text */}
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4 animate-fade-up">
                    <div>
                        <h1 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 to-gray-600">
                            Công ty đang theo dõi
                        </h1>
                        <p className="text-gray-500 mt-2 font-medium">Cập nhật tin tức và việc làm mới nhất từ các doanh nghiệp bạn quan tâm.</p>
                    </div>
                    <div className="bg-white px-5 py-2 rounded-full shadow-sm border border-gray-200 text-sm font-bold text-blue-600 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                        {companies.length} Doanh nghiệp
                    </div>
                </div>

                {companies.length === 0 ? (
                    <div className="bg-white rounded-3xl p-16 text-center shadow-lg border border-gray-100 animate-fade-up">
                        <div className="w-24 h-24 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                            <span className="material-symbols-outlined text-5xl text-blue-400">domain_disabled</span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Danh sách trống</h3>
                        <p className="text-gray-500 mt-2 mb-8">Bạn chưa theo dõi công ty nào. Hãy khám phá thêm nhé!</p>
                        <button onClick={() => navigate('/companies')} className="btn-signature px-8 py-3 text-sm shadow-blue-500/20">Khám phá ngay</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {companies.map((comp, idx) => {
                            const profile = comp.profile || {};
                            return (
                                <div 
                                    key={comp._id} 
                                    onClick={() => navigate(`/candidate/companies/${comp._id}`)} 
                                    className="group bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col h-full relative overflow-hidden animate-fade-up"
                                    style={{ animationDelay: `${idx * 0.05}s` }}
                                >
                                    {/* Decorative Top Border */}
                                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                    {/* Header: Logo & Info */}
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-16 h-16 rounded-xl border border-gray-100 bg-white p-1 shadow-sm flex items-center justify-center overflow-hidden shrink-0 group-hover:border-blue-200 transition-colors">
                                            {profile.avatar ? (
                                                <img src={profile.avatar} alt="Logo" className="w-full h-full object-contain rounded-lg" />
                                            ) : (
                                                <span className="text-2xl font-bold text-gray-400">{(profile.companyName || comp.name || "C").charAt(0)}</span>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-bold text-lg text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                                                {profile.companyName || comp.name}
                                            </h3>
                                            <p className="text-sm text-gray-500 truncate">{profile.industry || "Đa ngành"}</p>
                                            <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                                                <span className="material-symbols-outlined text-[14px]">location_on</span>
                                                <span className="truncate">{profile.location || "Toàn quốc"}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="mt-auto pt-4 border-t border-gray-50 flex justify-between items-center">
                                        <span className="text-xs font-medium text-gray-500 bg-gray-50 px-2 py-1 rounded-md">
                                            {comp.size || "Quy mô ẩn"}
                                        </span>
                                        <button 
                                            onClick={(e) => handleUnfollow(comp._id, e)}
                                            className="flex items-center gap-1 text-xs font-semibold text-red-500 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[14px]">remove_circle_outline</span> Bỏ theo dõi
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}